import * as React from 'react';
import { useSelector } from 'react-redux';

import Router from 'next/router';
import { pathToRegexp } from 'path-to-regexp';

import { selectProfileMeta } from '@portal/store/src/user/selectors';

import { useDebounce } from '@/utils/hooks/use-debounce';
import { nonAuthenticatedRoutes } from '@/utils/non-authenticated-routes';

import routeRules from '../route-rules';
import type { ApplicationState } from '../store';

import { resolveGuards } from './helpers';
import { verifyUserAccess } from './helpers/verify-user-access';
import type { RouteGuards } from './route-guards';

function findMatchingAuthRoute(asPath: string | undefined) {
  const pathWithoutQS = asPath?.split(/[?#]/)[0];

  if (pathWithoutQS) {
    return routeRules.find(({ pattern }) =>
      pathToRegexp(pattern).test(pathWithoutQS)
    );
  }
}

type AccessGuardProps = {
  children: React.ReactNode;
};

/**
 * This guard utilizes the role, scopes and guards set in route-with-auth.js
 * to determine if a user has access to a route.
 */
export const AccessGuard: React.FC<AccessGuardProps> = ({ children }) => {
  const isNonAuthenticatedRoute = nonAuthenticatedRoutes.has(Router.pathname);

  const [allowRender, setAllowRender] = React.useState(false);

  const state = useSelector((state: ApplicationState) => state);
  const profileMeta = useSelector(selectProfileMeta);

  const { asPath } = Router;
  const { roles } = profileMeta ?? {};

  // If roles update before router path, it can cause the page to throw a
  // forbidden error prematurely.
  //
  // This is especially relevant when a multi user toggles between experiences.
  // To resolve this, we debounce triggerAccessGuard to ensure the roles and
  // router path have finished their re-renders before re-evaluating a routes access.

  const triggerAccessGuard = useDebounce(
    JSON.stringify({ asPath, isNonAuthenticatedRoute, roles }),
    1000
  );

  React.useEffect(() => {
    if (isNonAuthenticatedRoute) {
      setAllowRender(true);

      return;
    }

    if (!profileMeta) {
      return;
    }

    const matchingAuthRoute = findMatchingAuthRoute(Router.asPath);

    const passesRouteGuardGuardsBool = resolveGuards(
      state,
      profileMeta,
      matchingAuthRoute?.guards as RouteGuards[]
    );

    if (
      matchingAuthRoute &&
      !verifyUserAccess(
        matchingAuthRoute,
        profileMeta.roles,
        profileMeta.scopes,
        profileMeta.featureFlags,
        passesRouteGuardGuardsBool
      )
    ) {
      Router.push('/forbidden');
    } else {
      setAllowRender(true);
    }
  }, [triggerAccessGuard]);

  if (allowRender) {
    return <>{children}</>;
  }

  return null;
};
